import { useState, useMemo } from "react";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";

// C.2 — Marcas Cotizadas: 6 categorías en orden fijo (mismo que backend)
export const MARCAS_COTIZADAS_CATEGORIES = [
  { key: "disyuntores",   label: "Disyuntores y Contactores", placeholder: "ej: HYUNDAI / SCHNEIDER / SIEMENS" },
  { key: "cables",        label: "Cables",                    placeholder: "ej: INDUSCABOS / CORDEIRO / MEGACABOS" },
  { key: "mecanismos",    label: "Mecanismos",                placeholder: "ej: BTICINO / SIMON / ILUMI" },
  { key: "tableros",      label: "Tableros",                  placeholder: "ej: SCHNEIDER / ABB / HAGER" },
  { key: "bandejas",      label: "Bandejas",                  placeholder: "ej: HIMEL / SAMET / VALMEC" },
  { key: "electroductos", label: "Electroductos y Cajas",     placeholder: "ej: TIGRE / ARGO / ELEKTRON" },
];

// Heurística simple para parsear string legacy → split (PDF REV5 C.2)
// Solo sugiere cuando la palabra clave es razonablemente unívoca.
const KEYWORDS = {
  cables:        ["induscabos", "cordeiro", "megacabos", "prysmian", "nexans", "cable"],
  disyuntores:   ["hyundai", "schneider", "siemens", "abb", "legrand", "chint", "disyuntor", "contactor"],
  tableros:      ["tigre", "argo", "elektron", "hager", "tablero"],
  mecanismos:    ["bticino", "simon", "ilumi", "jeluz", "mecanismo"],
  bandejas:      ["himel", "samet", "valmec", "bandeja"],
  electroductos: ["electroducto", "ductomatic", "caja"],
};

function reorganizeLegacy(legacyStr) {
  const out = { disyuntores: "", cables: "", mecanismos: "", tableros: "", bandejas: "", electroductos: "" };
  if (!legacyStr) return out;
  // Tokenizamos por comas y barras (separadores típicos del campo legacy)
  const tokens = legacyStr.split(/[,/]/).map(t => t.trim()).filter(Boolean);
  for (const tok of tokens) {
    const lower = tok.toLowerCase();
    let bestCat = null;
    for (const [cat, keys] of Object.entries(KEYWORDS)) {
      if (keys.some(k => lower.includes(k))) {
        bestCat = cat;
        break;
      }
    }
    if (bestCat) {
      out[bestCat] = out[bestCat] ? `${out[bestCat]} / ${tok}` : tok;
    }
  }
  return out;
}

export function MarcasCotizadasSplitField({ form, setForm }) {
  const split = form.marcas_cotizadas_split || {};
  const legacy = (form.marcas_cotizadas || "").trim();
  const hasSplit = useMemo(
    () => MARCAS_COTIZADAS_CATEGORIES.some(c => (split[c.key] || "").trim()),
    [split]
  );
  const isLegacyOnly = !hasSplit && !!legacy;
  const [showLegacyHelper, setShowLegacyHelper] = useState(isLegacyOnly);

  const updateCat = (key, value) => {
    setForm(f => ({
      ...f,
      marcas_cotizadas_split: { ...(f.marcas_cotizadas_split || {}), [key]: value },
    }));
  };

  const handleReorganizar = () => {
    const suggested = reorganizeLegacy(legacy);
    // Solo aplicar las claves no vacías como sugerencia editable
    setForm(f => ({
      ...f,
      marcas_cotizadas_split: { ...(f.marcas_cotizadas_split || {}), ...suggested },
    }));
  };

  return (
    <div className="md:col-span-2" data-testid="marcas-cotizadas-split-field">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <label className="block text-xs font-bold text-[#1A4B7A] uppercase tracking-wide">
          Marcas Cotizadas <span className="font-normal text-gray-400 normal-case">(por categoría)</span>
        </label>
        {isLegacyOnly && (
          <button
            type="button"
            onClick={() => setShowLegacyHelper(s => !s)}
            className="flex items-center gap-1 text-[10px] text-amber-700 hover:bg-amber-50 px-2 py-1 rounded border border-amber-200"
            data-testid="toggle-legacy-helper-btn"
          >
            {showLegacyHelper ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {showLegacyHelper ? "Ocultar formato anterior" : "Ver formato anterior"}
          </button>
        )}
      </div>

      {showLegacyHelper && legacy && (
        <div className="mb-3 border border-amber-200 bg-amber-50 rounded-xl p-3" data-testid="legacy-marcas-helper">
          <p className="text-[10px] uppercase font-bold text-amber-700 tracking-wider mb-1">
            Formato anterior — podés migrarlo abajo
          </p>
          <p className="text-xs text-amber-900 italic mb-2">{legacy}</p>
          <button
            type="button"
            onClick={handleReorganizar}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 px-2.5 py-1 rounded-lg"
            data-testid="reorganizar-marcas-btn"
          >
            <Sparkles size={11} /> Reorganizar automáticamente
          </button>
          <p className="text-[9px] text-amber-600 mt-1 italic">
            Heurística por palabras clave. Revisá y ajustá los campos antes de guardar.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-yellow-50/40 border border-gray-200 rounded-xl p-3">
        {MARCAS_COTIZADAS_CATEGORIES.map(cat => (
          <div key={cat.key}>
            <label className="block text-[10px] font-semibold text-gray-600 mb-0.5">
              {cat.label}
            </label>
            <input
              type="text"
              value={split[cat.key] || ""}
              onChange={e => updateCat(cat.key, e.target.value)}
              placeholder={cat.placeholder}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/30 bg-white"
              data-testid={`marcas-input-${cat.key}`}
            />
          </div>
        ))}
      </div>
      <p className="text-[9px] text-gray-400 italic mt-1">
        Las categorías vacías no aparecen en el PDF ni en el Excel.
      </p>
    </div>
  );
}
